/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { devModeService, DevModeSettings } from '../services/devModeService';

interface DevModeStore {
  // State
  settings: DevModeSettings;
  commandHistory: string[];
  commandHistoryIndex: number;

  // Actions
  toggleDevMode: () => void;
  togglePanel: (panel: keyof DevModeSettings['panels']) => void;
  toggleDisableTutorial: () => void;
  setActiveTab: (tab: string) => void;
  updatePanelPosition: (panel: string, position: { x: number; y: number }) => void;
  executeCommand: (command: string) => { success: boolean; message: string; data?: any } | null;
  navigateCommandHistory: (direction: 'up' | 'down') => string | null;
  setToastCallback: (callback: (message: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void) => void;
  setGameRefs: (refs: Record<string, unknown>) => void;
}

export const useDevModeStore = create<DevModeStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      settings: devModeService.getSettings(),
      commandHistory: [],
      commandHistoryIndex: -1,
      
      // Actions
      toggleDevMode: () => {
        devModeService.toggleDevMode();
        set({ settings: devModeService.getSettings() });
      },
      
      togglePanel: (panel) => {
        devModeService.togglePanel(panel);
        set({ settings: devModeService.getSettings() });
      },

      toggleDisableTutorial: () => {
        devModeService.toggleDisableTutorial();
        set({ settings: devModeService.getSettings() });
      },

      setActiveTab: (tab) => {
        devModeService.setActiveTab(tab);
        set({ settings: devModeService.getSettings() });
      },
      
      updatePanelPosition: (panel, position) => {
        devModeService.updatePanelPosition(panel, position);
        set({ settings: devModeService.getSettings() });
      },
      
      executeCommand: (command) => {
        if (!command.trim()) return null;
        
        // Add to history
        devModeService.addToConsoleHistory(command);
        set(state => ({
          commandHistory: [command, ...state.commandHistory],
          commandHistoryIndex: -1
        }));
        
        // Execute command
        return devModeService.executeCommand(command);
      },
      
      navigateCommandHistory: (direction) => {
        const state = get();
        const history = devModeService.getConsoleHistory();
        if (history.length === 0) return null;
        
        let newIndex = state.commandHistoryIndex;
        if (direction === 'up') {
          newIndex = Math.min(state.commandHistoryIndex + 1, history.length - 1);
        } else {
          newIndex = Math.max(state.commandHistoryIndex - 1, -1);
        }
        
        set({ commandHistoryIndex: newIndex });
        return newIndex >= 0 ? history[newIndex] : '';
      },
      
      setToastCallback: (callback) => {
        devModeService.setToastCallback(callback);
      },
      
      setGameRefs: (refs) => {
        devModeService.setGameRefs(refs);
      }
    })),
    {
      name: 'dev-mode-store',
    }
  )
);

// Deferred subscription to prevent initialization loops
let unsubscribe: (() => void) | null = null;

export const initializeDevModeSubscription = () => {
  if (unsubscribe) return; // Already initialized
  
  let isUpdating = false;
  unsubscribe = devModeService.subscribe((newSettings) => {
    if (!isUpdating) {
      isUpdating = true;
      // Use setTimeout to defer state update to next tick
      setTimeout(() => {
        useDevModeStore.setState({ settings: newSettings });
        isUpdating = false;
      }, 0);
    }
  });
  
  return unsubscribe;
};

// Selectors
export const selectDevModeEnabled = (state: DevModeStore) => state.settings.enabled;
export const selectDevModePanels = (state: DevModeStore) => state.settings.panels;
export const selectActiveTab = (state: DevModeStore) => state.settings.layout.activeTab;
export const selectConsoleHistory = (state: DevModeStore) => devModeService.getConsoleHistory();
export const selectDisableTutorial = (state: DevModeStore) => state.settings.disableTutorial;